import React from 'react';

import { FloatingFocusManager } from '@floating-ui/react';
import { AnimatePresence } from 'framer-motion';

import { IconChevronDown } from '@pkg/assets/icons';
import { currencyName, Portal, PortalArea, useCurrencySelectorPopover } from '@pkg/components';

import { Menu, type MenuProps } from './parts';

import { Action, ArrowWrapper, MenuPopover, Name, StyledLocalCurrencySelector } from './LocalCurrencySelector.style';

const DEFAULT_ARIA_LABEL = 'Select a different currency';

export interface LocalCurrencySelectorProps {
    selected?: MenuProps['selected'];
    ariaLabel?: string;
    disabled?: boolean;
    onSelect?: MenuProps['onSelect'];
}

export function LocalCurrencySelector({
    selected = 'usd',
    ariaLabel = DEFAULT_ARIA_LABEL,
    disabled = false,
    onSelect,
}: LocalCurrencySelectorProps) {
    const {
        open,
        onOpenChange,
        context,
        setReference,
        setFloating,
        floatingStyles,
        getReferenceProps,
        getFloatingProps,
    } = useCurrencySelectorPopover();

    const handleSelect: LocalCurrencySelectorProps['onSelect'] = (currency) => {
        onOpenChange(false);
        onSelect?.(currency);
    };

    const isDisabled = disabled || !onSelect;

    const dropdownMarkup = open ? (
        <Portal destination={PortalArea.Transition}>
            <FloatingFocusManager context={context} modal={false}>
                <MenuPopover ref={setFloating} style={floatingStyles} {...getFloatingProps()}>
                    <Menu selected={selected} onSelect={handleSelect} />
                </MenuPopover>
            </FloatingFocusManager>
        </Portal>
    ) : null;

    // TODO: Cannot use `<CommonAction />` until we update the `ref` type.
    return (
        <StyledLocalCurrencySelector>
            <Action ref={setReference} aria-label={ariaLabel} disabled={isDisabled} {...getReferenceProps()}>
                <Name>{currencyName[selected]}</Name>

                <ArrowWrapper>
                    <IconChevronDown />
                </ArrowWrapper>
            </Action>

            <AnimatePresence>{dropdownMarkup}</AnimatePresence>
        </StyledLocalCurrencySelector>
    );
}
